/* eslint-disable max-lines */
import { memo, useCallback, useContext, useLayoutEffect, useMemo, useState, type FunctionComponent } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import { useLazyQuery, useMutation, type ApolloCache } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
// Skillmore UI Components
import { ExportFormat } from '@empathco/ui-components/src/models/exportFormat';
import { injectParams } from '@empathco/ui-components/src/helpers/path';
import { mapChunks } from '@empathco/ui-components/src/helpers/intl';
import useQueryObject from '@empathco/ui-components/src/hooks/useQueryObject';
import useQueryCounted from '@empathco/ui-components/src/hooks/useQueryCounted';
import useMutationMethod from '@empathco/ui-components/src/hooks/useMutationMethod';
import CardTitle from '@empathco/ui-components/src/elements/CardTitle';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
import YesNoButtonGroup from '@empathco/ui-components/src/elements/YesNoButtonGroup';
// local imports
import { UPDATE_DEV_PLAN } from '../graphql/UpdateDevPlan';
import { DELETE_DEV_PLAN } from '../graphql/DeleteDevPlan';
import { DEV_PLAN_COURSES_QUERY } from '../graphql/DevPlanCourses';
import { DEV_PLAN_ADVISORS_QUERY } from '../graphql/DevPlanAdvisors';
import { DEV_PLAN_OPPORTUNITIES_QUERY } from '../graphql/DevPlanOpportunities';
import { DEV_PLAN_ACTIVITIES_QUERY } from '../graphql/DevPlanActivities';
import { DEV_PLAN_PROGRESSES_QUERY } from '../graphql/DevPlanProgresses';
import { EMPLOYEE_DEV_PLAN_QUERY } from '../graphql/EmployeeDevPlan';
import { EMPLOYEE_PROGRESS_QUERY } from '../graphql/EmployeeProgress';
import { EMPLOYEE_DEV_PLAN_ACTIVITIES_QUERY } from '../graphql/EmployeeDevPlanActivities';
import {
  DevPlanCourseType, DevPlanAdvisorType, DevPlanOpportunityType, Opportunity, CohortSkillStatus, SkillActivity,
  DevPlanCoursesDocument, DevPlanAdvisorsDocument, DevPlanOpportunitiesDocument, DevPlanActivitiesDocument,
  DevPlanProgressesDocument, EmployeeDevPlanDocument, EmployeeProgressDocument, EmployeeDevPlanActivitiesDocument,
  EmployeeDevPlanQuery, EmployeeProgressQuery,
  UpdateDevPlanDocument, DeleteDevPlanDocument
} from '../graphql/types';
import {
  AnyDevPlanAdvisor, AnyDevPlanCourse, AnyDevPlanOpportunity,
  DevPlanAdvisor, DevPlanCourse, DevPlanDetails, DevPlanOpportunity, DevPlanProgress, DevPlanTarget
} from '../graphql/customTypes';
import { SkillLevel, SKILL_LEVEL_REGULAR } from '../models/skill';
import {
  PATH_JOB, PATH_MY_OPPORTUNITY, PATH_MY_SKILLS,
  PATH_HR_COHORT, PATH_HR_DEV_PLANS, PATH_HR_DEV_PLAN_EDITOR, PATH_MY_DEV_PLAN_EDITOR, PATH_SKILL
} from '../config/paths';
import useCustomerSettings from '../config/customer';
import useExportDevPlan from '../hooks/useExportDevPlan';
import useExportDevPlanProgress from '../hooks/useExportDevPlanProgress';
import useEmployeeDevPlanActions from '../hooks/useEmployeeDevPlanActions';
import { getCorortSkillsHasLevels, getTargetSkills } from '../helpers/graphql';
import { GlobalContext } from '../context/global';
import DevPlanAvatar from '../elements/DevPlanAvatar';
import SkillChip from '../elements/SkillChip';
import DevPlanTimestampStatus from '../elements/DevPlanTimestampStatus';
import DevPlanActionSnackbars from '../elements/DevPlanActionSnackbars';
import ReviewBar from '../elements/ReviewBar';
import CoursesAndAdvisorsGrid from '../v3/CoursesAndAdvisorsGrid';
import EmployeeProgressPopup from '../v3/EmployeeProgressPopup';
import DevPlanEmployeesTable from '../v3/DevPlanEmployeesTable';
// SCSS imports
import { details } from './DevPlanReview.module.scss';

const updateCache = (cache: ApolloCache<unknown>, _isMyPlan: boolean, _onDelete: boolean) => {
  // if (isMyPlan) {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeDevplans' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeDevplan' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'employeeProgress' });
  // }
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'devplans' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'devplan' });
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'cohort' });
  // if we add dev_plan info to the cohort cards:
  // if (onDelete) cache.evict({ id: 'ROOT_QUERY', fieldName: 'cohorts' });
};

type DevPlanReviewProps = {
  readonly devplan?: DevPlanDetails | null;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
  readonly isMyPlan?: boolean;
  // for Storybook only
  readonly testAction?: number;
}

const DevPlanReview: FunctionComponent<DevPlanReviewProps> = ({
  devplan,
  pending: parentPending = false,
  failed: parentFailed = false,
  isMyPlan = false,
  testAction
  // eslint-disable-next-line complexity, max-statements, max-lines-per-function
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { HAS_COURSES, HAS_MENTORING, HAS_DEV_PLAN_OPPORTUNITIES } = useCustomerSettings();
  const { exportDevPlan, DEV_PLAN_CSV_COLUMNS } = useExportDevPlan();
  const { exportDevPlanProgress, DEV_PLAN_PROGRESS_CSV_COLUMNS } = useExportDevPlanProgress();

  const {
    id: devplan_id, title: devplanTitle, created_at, updated_at, job, opportunity, cohort, employee,
    target_skills
  } = devplan || {} as DevPlanDetails;
  const targetCode = job?.code || (isMyPlan ? (opportunity as Opportunity)?.opportunity_match_id : opportunity?.id);
  const targetTitle = job?.title || opportunity?.title;
  const { id: cohort_id, title: cohortTitle, cohort_skills } = cohort || {};

  const hasLevels = useMemo(() => getCorortSkillsHasLevels(cohort_skills), [cohort_skills]);
  const targetSkills = useMemo(() => getTargetSkills(target_skills), [target_skills]);

  const { user: { data: user }, paths: { supvEmplPath } } = useContext(GlobalContext);
  const targetPath = (job && PATH_JOB) ||
    (isMyPlan && opportunity && PATH_MY_OPPORTUNITY) || // TODO: HRBP path for opportunity?
    undefined;

  // lazy load dev plan courses
  const {
    query: getCourses, pending: pendingCourses, failed: failedCourses, results: courses
  } = useQueryCounted({
    data: undefined as unknown as DevPlanCourse,
    key: 'devplanCourses',
    lazyQuery: useLazyQuery(DEV_PLAN_COURSES_QUERY as typeof DevPlanCoursesDocument)
  });
  // lazy load dev plan advisors
  const {
    query: getAdvisors, pending: pendingAdvisors, failed: failedAdvisors, results: advisors
  } = useQueryCounted({
    data: undefined as unknown as DevPlanAdvisor,
    key: 'devplanAdvisors',
    lazyQuery: useLazyQuery(DEV_PLAN_ADVISORS_QUERY as typeof DevPlanAdvisorsDocument)
  });
  // lazy load dev plan opportunities
  const {
    query: getOpportunities, pending: pendingOpportunities, failed: failedOpportunities, results: opportunities
  } = useQueryCounted({
    data: undefined as unknown as DevPlanOpportunity,
    key: 'devplanOpportunities',
    lazyQuery: useLazyQuery(DEV_PLAN_OPPORTUNITIES_QUERY as typeof DevPlanOpportunitiesDocument)
  });
  // lazy load dev plan activities
  const {
    query: getActivities, pending: pendingActivities, failed: failedActivities, results: activities
  } = useQueryCounted({
    data: undefined as unknown as SkillActivity,
    key: 'devplanActivities',
    lazyQuery: useLazyQuery(DEV_PLAN_ACTIVITIES_QUERY as typeof DevPlanActivitiesDocument)
  });
  // lazy load dev plan employees progress
  const { query: getEmployees, pending: pendingEmployees, failed: failedEmployees, results: employees } = useQueryCounted({
    data: undefined as unknown as DevPlanProgress,
    key: 'devplanProgresses',
    lazyQuery: useLazyQuery(DEV_PLAN_PROGRESSES_QUERY as typeof DevPlanProgressesDocument)
  });
  // lazy load employee-scoped dev plan data with per-item completion state (my plan only)
  const {
    query: getMyDevplan, pending: pendingMyDevplan, failed: failedMyDevplan, results: myDevplan
  } = useQueryObject({
    data: undefined as unknown as EmployeeDevPlanQuery['employeeDevplan'],
    key: 'employeeDevplan',
    flatResults: true,
    lazyQuery: useLazyQuery(EMPLOYEE_DEV_PLAN_QUERY as typeof EmployeeDevPlanDocument)
  });
  const {
    query: getMyProgress, pending: pendingMyProgress, failed: failedMyProgress, results: myProgress
  } = useQueryObject({
    data: undefined as unknown as EmployeeProgressQuery['employeeProgress'],
    key: 'employeeProgress',
    flatResults: true,
    lazyQuery: useLazyQuery(EMPLOYEE_PROGRESS_QUERY as typeof EmployeeProgressDocument)
  });
  const {
    query: getMyPlanActivities, pending: pendingMyPlanActivities, failed: failedMyPlanActivities,
    results: myPlanActivities
  } = useQueryCounted({
    data: undefined as unknown as SkillActivity,
    key: 'employeeDevplanActivities',
    lazyQuery: useLazyQuery(EMPLOYEE_DEV_PLAN_ACTIVITIES_QUERY as typeof EmployeeDevPlanActivitiesDocument)
  });

  const { mutate: updateDevPlan, loading: updatePending, failed: updateFailed } = useMutationMethod({
    mutation: useMutation(UPDATE_DEV_PLAN as typeof UpdateDevPlanDocument)
  });

  const { mutate: deleteDevPlan, loading: deletePending, failed: deleteFailed } = useMutationMethod({
    mutation: useMutation(DELETE_DEV_PLAN as typeof DeleteDevPlanDocument)
  });

  useLayoutEffect(() => {
    if (devplan_id) {
      getEmployees?.({ variables: { devplan_id } });
      if (isMyPlan) {
        getMyDevplan?.({ variables: { devplan_id } });
        getMyProgress?.({ variables: { devplan_id } });
        getMyPlanActivities?.({ variables: { devplan_id } });
      } else {
        getActivities?.({ variables: { devplan_id } });
        if (HAS_COURSES) getCourses?.({ variables: {
          devplan_id, type: DevPlanCourseType.courses, exclude_ids: '', target_skill_ids: ''
        } });
        if (HAS_MENTORING) getAdvisors?.({ variables: {
          devplan_id, type: DevPlanAdvisorType.advisors, exclude_ids: '', target_skill_ids: ''
        } });
        if (HAS_DEV_PLAN_OPPORTUNITIES) getOpportunities?.({ variables: {
          devplan_id, type: DevPlanOpportunityType.opportunities, exclude_ids: '', target_skill_ids: ''
        } });
      }
    }
  }, [
    devplan_id, isMyPlan, getActivities, getEmployees, getCourses, getAdvisors, getOpportunities,
    getMyDevplan, getMyProgress, getMyPlanActivities,
    HAS_COURSES, HAS_MENTORING, HAS_DEV_PLAN_OPPORTUNITIES
  ]);

  const { is_active } = (isMyPlan && myDevplan) || {};
  const myPlanActive = isMyPlan && Boolean(is_active);

  const planCourses: AnyDevPlanCourse[] | null | undefined = isMyPlan ? myProgress?.courses : courses;
  const planAdvisors: AnyDevPlanAdvisor[] | null | undefined = isMyPlan ? myProgress?.advisors : advisors;
  const planOpportunities: AnyDevPlanOpportunity[] | null | undefined = isMyPlan
    ? myProgress?.opportunities : opportunities;
  const planActivities = isMyPlan ? myPlanActivities : activities;
  const coursesPending = isMyPlan ? pendingMyProgress : pendingCourses;
  const advisorsPending = isMyPlan ? pendingMyProgress : pendingAdvisors;
  const opportunitiesPending = isMyPlan ? pendingMyProgress : pendingOpportunities;
  const coursesFailed = isMyPlan ? failedMyProgress : failedCourses;
  const advisorsFailed = isMyPlan ? failedMyProgress : failedAdvisors;
  const opportunitiesFailed = isMyPlan ? failedMyProgress : failedOpportunities;

  const {
    pendingAction,
    handleActivation,
    activatePending,
    activateFailed,
    handleCompleteCourse,
    courseCompletingId,
    completeCourseFailed,
    handleSelectActivity,
    activitySelectingId,
    handleCompleteActivity,
    activityCompletingId,
    activityFailed,
    handleCompleteAdvisor,
    advisorCompletingId,
    completeAdvisorFailed,
    handleRequestAdvisor,
    advisorRequestingId,
    requestFailed
  } = useEmployeeDevPlanActions({ devplan_id, is_active, advisors: planAdvisors, testAction });

  const updateTitle = useCallback(
    (newTitle?: string) => updateDevPlan({
      variables: { devplan_id, input: { title: newTitle ? newTitle : '' } },
      update: (cache) => updateCache(cache, isMyPlan, false)
    }), [devplan_id, isMyPlan, updateDevPlan]
  );

  const deleteThisDevPlan = useCallback(
    () => deleteDevPlan?.({
      variables: { devplan_id },
      update: (cache) => updateCache(cache, isMyPlan, true),
      onCompleted: () => navigate(isMyPlan ? PATH_MY_SKILLS : PATH_HR_DEV_PLANS)
    }),
    [devplan_id, isMyPlan, deleteDevPlan, navigate]
  );

  const link = useMemo(() => injectParams(isMyPlan ? PATH_MY_DEV_PLAN_EDITOR : PATH_HR_DEV_PLAN_EDITOR, {
    plan_id: devplan_id
  }), [devplan_id, isMyPlan]);

  const getExport = useCallback((format: ExportFormat, title: string) => {
    if (
      !devplan || !title || !targetSkills ||
      (HAS_COURSES && !planCourses) || (HAS_MENTORING && !planAdvisors) ||
      ((!HAS_COURSES || size(planCourses) < 1) && (!HAS_MENTORING || size(planAdvisors) < 1))
    ) return null;
    return exportDevPlan(
      format,
      title,
      map(DEV_PLAN_CSV_COLUMNS, (id) => formatMessage({ id })),
      targetSkills,
      (HAS_COURSES && planCourses) || [],
      (HAS_MENTORING && planAdvisors) || [],
      (HAS_DEV_PLAN_OPPORTUNITIES && planOpportunities) || [],
      size(planActivities) >= 1 ? planActivities : null
    );
  }, [
    devplan, targetSkills, planCourses, planAdvisors, planOpportunities, planActivities,
    HAS_COURSES, HAS_MENTORING, HAS_DEV_PLAN_OPPORTUNITIES, formatMessage,
    DEV_PLAN_CSV_COLUMNS, exportDevPlan
  ]);

  const getEmployeesExport = useCallback((format: ExportFormat, _title: string) => {
    if (
      (HAS_COURSES && !planCourses) || (HAS_MENTORING && !planAdvisors) ||
      !targetSkills || !employees || !devplanTitle
    ) return null;
    return exportDevPlanProgress(
      format,
      devplanTitle,
      map(DEV_PLAN_PROGRESS_CSV_COLUMNS, (id) => formatMessage({ id })),
      targetSkills,
      (HAS_COURSES && planCourses) || [],
      (HAS_MENTORING && planAdvisors) || [],
      (HAS_DEV_PLAN_OPPORTUNITIES && planOpportunities) || [],
      size(planActivities) >= 1 ? planActivities : null,
      employees
    );
  }, [
    employees, planCourses, planAdvisors, planOpportunities, planActivities, targetSkills, devplanTitle,
    HAS_COURSES, HAS_MENTORING, HAS_DEV_PLAN_OPPORTUNITIES, formatMessage,
    DEV_PLAN_PROGRESS_CSV_COLUMNS, exportDevPlanProgress
  ]);

  const handleSkillClick = useCallback((id: number, abbr: string) => {
    if (id && abbr) navigate(injectParams(PATH_SKILL, { skill_id: abbr }));
  }, [navigate]);

  const [empl, setEmpl] = useState<DevPlanProgress>();
  const [popupOpen, setPopupOpen] = useState(false);
  const handlePopupClose = useCallback(() => setPopupOpen(false), []);
  const handleEmployeeClick = useCallback((emp: DevPlanProgress) => {
    setEmpl(emp);
    setPopupOpen(true);
  }, []);

  const target: DevPlanTarget | null = (job && 'job') || (opportunity && 'opportunity') || null;
  const pending = parentPending || (isMyPlan ? pendingMyPlanActivities : pendingActivities);
  const failed = parentFailed || (isMyPlan ? failedMyPlanActivities : failedActivities);
  const disabled = pending || updatePending || deletePending || pendingAction ? true : undefined;

  return (
    <>
      {failed || pending ? (
        <CardTitle title={failed ? 'hr.dev_plan.title' : <Skeleton variant="text" width="12rem"/>}/>
      ) : (
        <ReviewBar
            titleLabel="hr.dev_plan.title.label"
            title={devplanTitle}
            link={link}
            avatar={isMyPlan ? undefined : <DevPlanAvatar/>}
            status={(
              <DevPlanTimestampStatus
                  compact={isMyPlan}
                  createdAt={created_at}
                  updatedAt={updated_at}
                  myOwn={isMyPlan ? user?.id === devplan?.owner?.id : undefined}
              />
            )}
            action={isMyPlan && devplan && !pendingMyDevplan && !failedMyDevplan && myDevplan ? (
              <YesNoButtonGroup
                  small
                  value={Boolean(is_active)}
                  yesLabel="common.tag.active"
                  noLabel="common.tag.inactive"
                  onChange={handleActivation}
                  disabled={disabled}
                  pendingValue={activatePending ? Boolean(is_active) : undefined}
              />
            ) : undefined}
            // pending // handled above
            disabled={pendingAction ? true : undefined}
            editLabel="hr.dev_plan.edit"
            onRename={updateTitle}
            renameLabel="hr.dev_plan.rename"
            renamePending={updatePending}
            renameFailed={updateFailed}
            renameError="hr.dev_plan.rename_error"
            onDelete={deleteThisDevPlan}
            deleteLabel="common.button.delete"
            deleteTitle="hr.dev_plan.delete_title"
            deleteQuestion="hr.dev_plan.delete_question"
            deletePending={deletePending}
            deleteFailed={deleteFailed}
            deleteError="hr.dev_plan.delete_error"
            onExport={getExport}
            exportDisabled={!devplan || !devplanTitle || !targetSkills ||
              (HAS_COURSES && !planCourses) || (HAS_MENTORING && !planAdvisors) ||
              (size(planCourses) < 1 && size(planAdvisors) < 1)}
        />
      )}
      {(failed && <FetchFailedAlert flat/>) || (pending && <LoadingPlaceholder flat/>) || (
        <>
          <Divider light/>
          <CoursesAndAdvisorsGrid
              isMyPlan={isMyPlan}
              courses={HAS_COURSES ? planCourses : []}
              advisors={HAS_MENTORING ? planAdvisors : []}
              opportunities={HAS_DEV_PLAN_OPPORTUNITIES ? planOpportunities : []}
              activities={planActivities}
              pendingCourses={HAS_COURSES ? coursesPending : false}
              pendingAdvisors={HAS_MENTORING ? advisorsPending : false}
              pendingOpportunities={HAS_DEV_PLAN_OPPORTUNITIES ? opportunitiesPending : false}
              failedCourses={HAS_COURSES ? coursesFailed : false}
              failedAdvisors={HAS_MENTORING ? advisorsFailed : false}
              failedOpportunities={HAS_DEV_PLAN_OPPORTUNITIES ? opportunitiesFailed : false}
              disabled={disabled}
              notFoundMessage="hr.dev_plan.empty"
              isSelected
              withEmployeeControls={myPlanActive ? true : undefined}
              withCompletedState={myPlanActive ? true : undefined}
              onCompleteCourse={myPlanActive ? handleCompleteCourse : undefined}
              onSelectActivity={myPlanActive ? handleSelectActivity : undefined}
              onCompleteActivity={myPlanActive ? handleCompleteActivity : undefined}
              onCompleteAdvisor={myPlanActive ? handleCompleteAdvisor : undefined}
              onRequestAdvisor={myPlanActive ? handleRequestAdvisor : undefined}
              courseCompletingId={courseCompletingId}
              activitySelectingId={activitySelectingId}
              activityCompletingId={activityCompletingId}
              advisorCompletingId={advisorCompletingId}
              advisorRequestingId={advisorRequestingId}
          />
          <CardSection
              bottom={HAS_MENTORING || HAS_DEV_PLAN_OPPORTUNITIES
                ? (!HAS_MENTORING || (!advisorsPending && !advisorsFailed)) &&
                  (!HAS_DEV_PLAN_OPPORTUNITIES || (!opportunitiesPending && !opportunitiesFailed))
                : !coursesPending && !coursesFailed}
          >
            <Box className={details}>
              <FormattedMessage
                  id="hr.dev_plan.target"
                  values={{
                    target,
                    title: targetPath && targetCode ? (
                      <StandardLink
                          text={targetTitle}
                          to={disabled ? undefined : injectParams(targetPath, { role_id: targetCode, opp_id: targetCode })}
                          variant="inherit"
                      />
                    ) : targetTitle
                  }}
              />
            </Box>
            {cohort ? (
              <>
                <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent="flex-start"
                    pt={0.25}
                    pb={1}
                >
                  <Box pr={2} className={details}>
                    <FormattedMessage id="hr.dev_plan.skills_criteria"/>
                  </Box>
                  {map(cohort_skills, ({ skill, skill_proficiency_level, status }) => status === CohortSkillStatus.required ? (
                    <SkillChip
                        key={skill.id}
                        plain
                        skill={skill}
                        level={hasLevels ? skill_proficiency_level as SkillLevel || SKILL_LEVEL_REGULAR : undefined}
                        disabled={disabled ? true : undefined}
                        onActivate={handleSkillClick}
                    />
                  ) : undefined)}
                </Box>
                <Box className={details}>
                  <FormattedMessage
                      id="hr.dev_plan.cohort"
                      values={{
                        bold: mapChunks,
                        title: cohort_id ? (
                          <StandardLink
                              text={cohortTitle}
                              to={disabled ? undefined : injectParams(PATH_HR_COHORT, { cohort_id })}
                              variant="inherit"
                          />
                        ) : cohortTitle
                      }}
                  />
                </Box>
              </>
            ) : undefined}
          </CardSection>
          <Divider light/>
          {cohort ? (
            <DevPlanEmployeesTable
                target={target}
                hasActivities={size(planActivities) >= 1}
                cohortTitle={cohortTitle}
                // route={supvEmplPath}
                employees={employees}
                pending={pendingEmployees}
                failed={failedEmployees}
                onClick={handleEmployeeClick}
                disabled={disabled}
                onExport={getEmployeesExport}
                exportDisabled={(HAS_COURSES && !planCourses) || (HAS_MENTORING && !planAdvisors) ||
                  !targetSkills || !employees || !devplanTitle}
            />
          ) : undefined}
          {devplan && (employee || empl) ? (
            <EmployeeProgressPopup
                isMyPlan={isMyPlan}
                route={supvEmplPath}
                devplan={devplan}
                activities={planActivities}
                employee={employee ? employees?.[0] : empl}
                pending={pendingEmployees}
                failed={failedEmployees}
                isOpen={popupOpen}
                onClose={employee ? undefined : handlePopupClose}
                onExport={getEmployeesExport}
                exportDisabled={(HAS_COURSES && !planCourses) || (HAS_MENTORING && !planAdvisors) ||
                  !targetSkills || !employees || !devplanTitle}
            />
          ) : undefined}
        </>
      )}
      {isMyPlan ? (
        <DevPlanActionSnackbars
            activateFailed={activateFailed}
            requestFailed={requestFailed}
            completeCourseFailed={completeCourseFailed}
            activityFailed={activityFailed}
            completeAdvisorFailed={completeAdvisorFailed}
        />
      ) : undefined}
    </>
  );
};

export default memo(DevPlanReview);
